import { Config } from 'config/envConfig';
import axios from 'axios';
import {
    faCopy,
    faFolder,
    faLink,
    faRobot,
    faUsers,
    faUsersGear,
    faUserTie,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect } from 'react';
import { alert, AlertType, Button, LoadingView } from '@quyva/shared-ui';
import { useState } from 'react';
import { getUrl } from '../../utils';
import { Table } from 'antd';
import { Information } from './information';
import { Toggle } from '@quyva/shared-ui';
import { truncateText } from '@quyva/common';

const getTelegramBotInfo = async (
    user,
    repoId,
    setBotData,
    setLoadingText,
    setBotAccess
) => {
    try {
        setLoadingText(`Loading telegram bot info...`);

        const dynamicUrl = getUrl(
            `http://${Config.NODE_SERVER_HOST}:${Config.NODE_SERVER_PORT}/telegram-support/get-telegram-bot-info?repoId=${repoId}`
        );
        const encodedToken = encodeURIComponent(user.token);

        const response = await axios.get(dynamicUrl, {
            headers: {
                'Content-Type': 'application/json',
                Authorization: encodedToken,
            },
        });

        setBotData(response.data);
        setBotAccess(response.data?.unrestricted_access);
    } catch (error) {
        alert(
            AlertType.ERROR,
            'Error fetching Telegram bot info: ' + error.message
        );
    } finally {
        setLoadingText('Loading...');
    }
};

const getTelegramGroupsInfo = async (
    user,
    repoId,
    setGroupsData,
    setLoadingText
) => {
    try {
        setLoadingText(`Loading telegram groups info...`);

        const dynamicUrl = getUrl(
            `http://${Config.NODE_SERVER_HOST}:${Config.NODE_SERVER_PORT}/telegram-support/get-telegram-groups-info?repoId=${repoId}`
        );
        const encodedToken = encodeURIComponent(user.token);

        const response = await axios.get(dynamicUrl, {
            headers: {
                'Content-Type': 'application/json',
                Authorization: encodedToken,
            },
        });

        setGroupsData(response.data || []);
    } catch (error) {
        alert(
            AlertType.ERROR,
            'Error fetching Telegram group info: ' + error.message
        );
    } finally {
        setLoadingText('Loading...');
    }
};

const setBotUnrestrictedAccess = async (
    user,
    repoId,
    unrestrictedAccess,
    setIsLoading,
    setLoadingText,
    setBotAccess
) => {
    try {
        setIsLoading(true);
        setLoadingText('Setting bot access...');

        const url = getUrl(
            `http://${Config.NODE_SERVER_HOST}:${Config.NODE_SERVER_PORT}/telegram-support/set-telegram-bot-unrestricted-access`
        );
        const encodedToken = encodeURIComponent(user.token);

        await axios.post(
            url,
            { repoId, unrestrictedAccess },
            {
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: encodedToken,
                },
            }
        );

        setBotAccess(unrestrictedAccess);
        alert(AlertType.SUCCESS, 'Bot access updated successfully');
    } catch (error) {
        alert(AlertType.ERROR, 'Failed to update bot access: ' + error.message);
    } finally {
        setIsLoading(false);
        setLoadingText('');
    }
};

const defaultTableSize = 4;

const GroupsDetails = ({ groupsData, copyToClipboard }) => {
    const pagination = {
        defaultPageSize: defaultTableSize,
        disabled: groupsData.length <= defaultTableSize,
    };

    const columns = [
        {
            title: <div className='text-texts-secondary300'>S No.</div>,
            dataIndex: 'index',
            key: 'index',
        },
        {
            title: (
                <div className='text-texts-secondary300'>
                    <span>Group Name</span>
                </div>
            ),
            dataIndex: 'name',
            key: 'sNo',
        },
        {
            title: (
                <div className='text-texts-secondary300'>
                    <FontAwesomeIcon icon={faLink} className='mr-2' />
                    <span>Joining Link</span>
                </div>
            ),
            dataIndex: 'link',
            key: 'link',
        },
    ];

    const dataSource = groupsData.map((data, index) => {
        const dataObj = {
            index: `${index + 1}.`,
            name: (
                <div className='w-full flex items-center'>
                    <div>{truncateText(data.name, 26)}</div>
                    <div>
                        <Button
                            className={`ml-2`}
                            hoverText={'Copy group name'}
                            text={null}
                            icon={faCopy}
                            type={'success'}
                            onClick={() => copyToClipboard(data.name)}
                        />
                    </div>
                </div>
            ),
            link: (
                <div className='w-full flex items-center'>
                    <div>
                        {data.url ? (
                            data.url
                        ) : (
                            <span className='text-texts-secondary300'>
                                Link Not Available
                            </span>
                        )}
                    </div>
                    {data.url && (
                        <Button
                            className={`ml-2`}
                            hoverText={'Copy group link'}
                            text={null}
                            icon={faCopy}
                            type={'success'}
                            onClick={() => copyToClipboard(data.url)}
                        />
                    )}
                </div>
            ),
        };
        return dataObj;
    });

    return (
        <div>
            <div className='w-full flex items-center font-semibold text-texts-secondary300 mb-3'>
                <FontAwesomeIcon
                    icon={faUsers}
                    className='mr-2'
                    title='Information'
                />
                <div>Telegram Groups</div>
                <span className='text-texts-tertiary400 ml-2'>
                    ( {groupsData.length} )
                </span>
            </div>
            <div className='flex-col items-center justify-center overflow-auto'>
                <Table
                    dataSource={dataSource}
                    columns={columns}
                    bordered={true}
                    pagination={
                        groupsData.length > defaultTableSize
                            ? pagination
                            : false
                    }
                    size='small'
                />
            </div>
        </div>
    );
};

const saveTelegramGreeting = async (user, repoId, greetingMessage, setIsSaving) => {
    try {
        setIsSaving(true);
        const url = getUrl(
            `http://${Config.NODE_SERVER_HOST}:${Config.NODE_SERVER_PORT}/telegram-support/update-telegram-bot-greeting`
        );
        const encodedToken = encodeURIComponent(user.token);
        await axios.put(
            url,
            { repoId, greetingMessage: greetingMessage === '' ? null : greetingMessage },
            {
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: encodedToken,
                },
            }
        );
        alert(AlertType.SUCCESS, 'Greeting message saved');
    } catch (error) {
        alert(AlertType.ERROR, 'Failed to save greeting: ' + error.message);
    } finally {
        setIsSaving(false);
    }
};

export const TelegramDetails = ({ user, repository }) => {
    const [isLoading, setIsLoading] = useState(true);
    const [loadingText, setLoadingText] = useState('Loading...');
    const [botData, setBotData] = useState(null);
    const [groupsData, setGroupsData] = useState([]);
    const [botAccess, setBotAccess] = useState([]);
    const [greetingMessage, setGreetingMessage] = useState('');
    const [isSavingGreeting, setIsSavingGreeting] = useState(false);

    const botName = botData?.name;
    const botOwner = botData?.owner_mail;

    const information = [
        {
            data: (
                <>
                    Please use bot name to mention in group to ask questions
                    related to repository. Example:
                    <span className='mx-1 font-semibold'>@{botName}</span>
                </>
            ),
            subData: null,
        },
        {
            data: (
                <>
                    You can also chat with the bot by directly searching
                    <span className='mx-1 font-semibold'>{botName}</span>
                    in telegram.
                </>
            ),
            subdata: null,
        },

        {
            data: (
                <>
                    You can create multiple groups for this repo, you just need
                    to follow few steps manually.
                </>
            ),
            subData: [
                <>Manually create a group in your telegram account</>,
                <>
                    Manually Add
                    <span className='mx-1 font-semibold'>{botName}</span>
                    to the group
                </>,
                <>
                    Provide the {botName} with
                    <span className='mx-1 font-semibold'>Admin privileges</span>
                    in the group
                </>,
            ],
        },
    ];

    useEffect(() => {
        if (repository.repoid) {
            const fetchTelegramDetails = async () => {
                setIsLoading(true);
                await getTelegramBotInfo(
                    user,
                    repository.repoid,
                    (data) => {
                        setBotData(data);
                        // Initialise greeting field: null → "" (will trigger LLM), "" → ""
                        setGreetingMessage(data?.greeting_message ?? '');
                    },
                    setLoadingText,
                    setBotAccess
                );
                await getTelegramGroupsInfo(
                    user,
                    repository.repoid,
                    setGroupsData,
                    setLoadingText
                );
                setIsLoading(false);
            };

            fetchTelegramDetails();
        }
    }, [repository.repoid]);

    const copyToClipboard = (value) => {
        navigator.clipboard
            .writeText(value)
            .then(() => {
                alert(AlertType.SUCCESS, `Copied to clipboard`);
            })
            .catch((err) => {
                alert(AlertType.ERROR, 'Failed to copy text');
            });
    };

    if (isLoading) {
        return (
            <div className='w-[40vw] h-[70vh] flex justify-center items-center overflow-none'>
                <LoadingView loadingText={loadingText} />
            </div>
        );
    }

    return (
        <div className='w-[40vw] min-h-[70vh] max-h-[70vh] flex flex-col p-8'>
            <div className='flex items-center mb-6'>
                <div className='w-1/2 font-semibold text-texts-secondary300'>
                    <label htmlFor='repoName'>
                        <FontAwesomeIcon
                            icon={faFolder}
                            className='mr-2'
                            title='Information'
                        />
                        <span>Repository</span>
                    </label>
                </div>

                <div className='w-full flex items-center'>
                    <div className='w-full'>{repository.name}</div>
                </div>
            </div>

            <div className='flex items-center mb-6'>
                <div className='w-1/2 font-semibold text-texts-secondary300'>
                    <label htmlFor='botName'>
                        <FontAwesomeIcon
                            icon={faRobot}
                            className='mr-2'
                            title='Information'
                        />
                        <span>Bot Name</span>
                    </label>
                </div>

                <div className='w-full flex items-center'>
                    <div>{botName}</div>
                    <Button
                        className={`ml-2`}
                        hoverText={'Copy bot name'}
                        text={null}
                        icon={faCopy}
                        type={'success'}
                        onClick={() => copyToClipboard(botName)}
                    />
                </div>
            </div>

            <div className='flex items-center mb-6'>
                <div className='w-1/2 font-semibold text-texts-secondary300'>
                    <label htmlFor='botName'>
                        <FontAwesomeIcon
                            icon={faUserTie}
                            className='mr-2'
                            title='Information'
                        />
                        <span>Bot Owner</span>
                    </label>
                </div>

                <div className='w-full flex items-center'>
                    <div>{botOwner}</div>
                    <Button
                        className={`ml-2`}
                        hoverText={'Copy bot name'}
                        text={null}
                        icon={faCopy}
                        type={'success'}
                        onClick={() => copyToClipboard(botOwner)}
                    />
                </div>
            </div>

            <div className='flex items-center mb-6'>
                <div className='w-2/4 font-semibold text-texts-secondary300'>
                    <label htmlFor='unrestrictedAccess'>
                        <FontAwesomeIcon
                            icon={faUsersGear}
                            className='mr-2'
                            title='Information'
                        />
                        <span>Unrestricted Bot Access</span>
                    </label>
                </div>

                <div className='w-full'>
                    <div className='relative w-full'>
                        <Toggle
                            checked={botAccess}
                            onChange={async (value) => {
                                await setBotUnrestrictedAccess(
                                    user,
                                    repository.repoid,
                                    value,
                                    setIsLoading,
                                    setLoadingText,
                                    setBotAccess
                                );
                            }}
                            toggleTextClassName={''}
                            disabled={botData?.owner_mail !== user?.email}
                            title={
                                botData?.owner_mail !== user?.email
                                    ? 'Only bot owner can change this setting'
                                    : `Change ${botName} access for telegram users`
                            }
                        />
                    </div>
                </div>
            </div>

            {/* Greeting Message */}
            <div className='flex items-start mb-6'>
                <div className='w-1/2 font-semibold text-texts-secondary300 pt-1'>
                    <label htmlFor='telegramGreeting'>Greeting Message</label>
                    <p className='text-xs font-normal text-texts-secondary300 mt-1'>
                        Sent when a user starts a chat with the bot (/start). Leave blank to auto-generate from persona.
                    </p>
                </div>
                <div className='w-full flex flex-col gap-2'>
                    <textarea
                        id='telegramGreeting'
                        className='w-full rounded border border-backgrounds-primary200 bg-backgrounds-white text-texts-secondary300 px-3 py-2 text-sm resize-y focus:outline-none focus:ring-1 focus:ring-buttons-primary'
                        rows={3}
                        maxLength={500}
                        value={greetingMessage}
                        placeholder="e.g. Hi! I'm here to help you with your queries."
                        disabled={botData?.owner_mail !== user?.email}
                        title={
                            botData?.owner_mail !== user?.email
                                ? 'Only the bot owner can change this setting'
                                : ''
                        }
                        onChange={(e) => setGreetingMessage(e.target.value)}
                    />
                    <div className='flex items-center justify-between'>
                        <span className='text-xs text-texts-secondary300'>
                            {greetingMessage.length}/500
                        </span>
                        <Button
                            text={isSavingGreeting ? 'Saving…' : 'Save Greeting'}
                            type='success'
                            disabled={isSavingGreeting || botData?.owner_mail !== user?.email}
                            onClick={() =>
                                saveTelegramGreeting(
                                    user,
                                    repository.repoid,
                                    greetingMessage,
                                    setIsSavingGreeting
                                )
                            }
                        />
                    </div>
                </div>
            </div>

            <div className='mb-6'>
                <GroupsDetails
                    groupsData={groupsData}
                    copyToClipboard={copyToClipboard}
                />
            </div>

            <Information infoData={information} />
        </div>
    );
};
